import { useCallback } from 'react';
import {
	useAxiosInstance,
	saveUserSettingQuery,
	CommonSettingKey,
} from '@gov-nx/api';
import type {
	SearchAllRecentVisited,
	SearchAllResultType,
} from '@gov-nx/module/page';
import {
	useAuthStore,
	useUserStore,
	useUserStoreRecentVisited,
} from '@gov-nx/store';

export const useSearchRecentVisited = () => {
	const activeReza = useAuthStore('activeReza');
	const setUserSettingRecentVisited = useUserStore(
		'setUserSettingRecentVisited'
	);
	const recentVisited = useUserStoreRecentVisited();
	const { axiosInstance } = useAxiosInstance();
	const activeRezaKod = activeReza?.kod ?? '';

	const addRecentVisited = useCallback(
		async (item: SearchAllRecentVisited) => {
			const personVisited = recentVisited.filter(
				(v) => v.rezaKod === activeRezaKod
			);

			const withoutDupes = personVisited.filter(
				(v) =>
					!(
						v.type === item.type &&
						JSON.stringify(v.actionParams) === JSON.stringify(item.actionParams)
					)
			);

			const updatedForPerson = [...withoutDupes, { ...item }];

			const lastTenForPerson = updatedForPerson.slice(-10);
			const otherPersons = recentVisited.filter(
				(v) => v.rezaKod !== activeRezaKod
			);

			const merged = [...otherPersons, ...lastTenForPerson];

			setUserSettingRecentVisited(merged);

			await saveUserSettingQuery(axiosInstance, {
				key: CommonSettingKey.RecentVisited,
				value: merged,
			});

			return merged;
		},
		[recentVisited, setUserSettingRecentVisited, activeRezaKod, axiosInstance]
	);

	const removeRecentVisited = useCallback(
		async (
			type: SearchAllResultType,
			actionParams: SearchAllRecentVisited['actionParams']
		) => {
			const personVisited = recentVisited.filter(
				(v) =>
					v.rezaKod === activeRezaKod &&
					!(
						v.type === type &&
						JSON.stringify(v.actionParams) === JSON.stringify(actionParams)
					)
			);

			const otherPersons = recentVisited.filter(
				(v) => v.rezaKod !== activeRezaKod
			);

			const merged = [...otherPersons, ...personVisited];

			setUserSettingRecentVisited(merged);

			await saveUserSettingQuery(axiosInstance, {
				key: CommonSettingKey.RecentVisited,
				value: merged,
			});

			return merged;
		},
		[recentVisited, setUserSettingRecentVisited, activeRezaKod, axiosInstance]
	);

	const filteredRecentVisited = recentVisited.filter(
		(v) => v.rezaKod === activeRezaKod
	);

	return {
		addRecentVisited,
		recentVisited: filteredRecentVisited,
		removeRecentVisited,
	};
};
